import { defineComponent as _defineComponent } from 'vue'
import type { Maybe } from '@/helpers/types';

import { computed, onMounted, ref, watch } from 'vue';
import { useDocumentVisibility } from '@vueuse/core';

import {
	SnowplowContext,
	SnowplowModuleContext,
	SnowplowTitleContextGraphql,
} from '@/helpers/tracking/providers/snowplow-contexts';

import { SraEventAction, SraPlacement } from '@/@types/graphql-types';
import { ImpressionTrackingEvents } from '@/enums/events';

import type { SponsoredAdFragment } from '@/pages/graphql/fragments/SponsoredAd.fragment';
import type { SingleTitleSR } from '@/components/sponsored-recommendations/types';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { useBackendTracking } from '@/helpers/composables/useBackendTracking';
import { useRoutingStore } from '@/helpers/composables/useStores';

/** Viewing thresholds at which we want to track. */
type TrackingWatchedPoints = (typeof TrackingWatchedPoints)[number];

export default /*#__PURE__*/_defineComponent({
  __name: 'SponsoredRecommendationVideo',
  props: {
    sponsoredAd: null,
    isMuted: { type: Boolean, default: false },
    lazy: { type: Boolean, default: true },
    showOverlay: { type: Boolean, default: false },
    additionalContexts: { default: () => [] },
    sraPlacement: null
  },
  emits: ["onPosterClickOut", "imageError", "onVideoEnded"],
  setup(__props: any, { emit: emits }: { emit: ({
	(e: 'onPosterClickOut'): void;
	(e: 'imageError', src: Maybe<string>): void;
	(e: 'onVideoEnded', hasVideoEnded: boolean): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { sponsoredAd: SponsoredAdFragment, isMuted: boolean, lazy: boolean, showOverlay: boolean, additionalContexts: (SnowplowModuleContext | SnowplowContext)[], sraPlacement?: SraPlacement };

const TrackingWatchedPoints = [0, 25, 50, 75, 90, 100] as const;





const { trackSraEvent } = useBackendTracking();
const visibility = useDocumentVisibility();
const { activeRoute } = useRoutingStore();

const sponsoredCampaign = computed(() => props.sponsoredAd?.campaign);
const campaignNode = computed<SingleTitleSR | null>(
	() => (sponsoredCampaign.value?.node ?? null) as SingleTitleSR | null
);

const disclaimerText = computed(() => sponsoredCampaign.value?.disclaimerText);

const isVideoAsGifCampaign = computed(() => sponsoredCampaign.value?.playSRVideoAsGif);

// Hack: display the video as gif in top of container for search page placement only
const shouldPositionVidAsGifToTop = computed(() => {
	return activeRoute.value.name.includes('search') && isVideoAsGifCampaign.value;
});

/* Watch Document Visibility */
watch(visibility, (current, previous) => {
	if (videoElement && current === 'visible' && previous === 'hidden') {
		videoElement.value?.play();
		return;
	}

	if (videoElement && current === 'hidden' && previous === 'visible') {
		videoElement.value?.pause();
		return;
	}
});

/* VIDEO TRACKING */
const videoElement = ref<HTMLVideoElement | null>(null);

/* VIDEO CONTROL*/
const isFading = ref(false);
const isMuted = ref(true);

const volumeIcon = computed(() => {
	const filename = isMuted.value ? 'volume-xmark' : 'volume-high';
	return `/${ASSETS_DIR}/img/sponsored-recommendation-assets/${filename}.svg`;
});

function onVideoStateChanged(eventName: string) {
	if (eventName !== 'play') return;
	setTimeout(() => (isFading.value = true), 1500);
}

function onMouseEnter() {
	isFading.value = false;
}

function onMouseLeave() {
	isFading.value = true;
}

function onVisibilityPlaybackControl(isVisible: boolean) {
	if (!videoElement.value) return;
	isVisible ? videoElement.value.play() : videoElement.value.pause();
}

function toggleMute() {
	isMuted.value = !isMuted.value;

	if (videoElement.value) {
		videoElement.value.muted = isMuted.value;
	}

	if (!campaignNode.value) {
		return;
	}

	const titleContext = new SnowplowTitleContextGraphql(
		campaignNode.value?.objectId,
		campaignNode.value?.objectType,
		campaignNode.value?.__typename === 'Season' ? campaignNode.value?.content.seasonNumber : null
	);

	// legacy event as long as there doesnt exist a SraEventAction.ToggleMute.
	TrackingHelper.trackEvent(
		ImpressionTrackingEvents.SPONSORED_RECOMMENDATIONS,
		{
			action: 'toggle_mute',
			value: isMuted.value ? 1 : 0,
		},
		[titleContext]
	);
}

/* Track Duration Play */
const videoDuration = ref(0);
const lastTrackedPoint = ref<TrackingWatchedPoints | -1>(-1);

function setVideoDuration() {
	if (!videoElement.value) return;
	videoDuration.value = videoElement.value.duration;
}

function onTimeUpdate() {
	if (lastTrackedPoint.value >= 100) return;

	const percentageViewed = Math.ceil(((videoElement.value?.currentTime ?? 0) / videoDuration.value) * 100);
	const nextTrackingPoint = TrackingWatchedPoints.find(point => point > lastTrackedPoint.value);

	if (nextTrackingPoint == null || percentageViewed < nextTrackingPoint) return;

	lastTrackedPoint.value = nextTrackingPoint;

	trackSraEvent({
		action: SraEventAction.VideoView,
		sponsoredAd: () => props.sponsoredAd,
		progress: nextTrackingPoint,
	});
}

/**
 * `this.onTimeUpdate` doesn't always fire at 100 before restarting the loop.
 * So we trigger it manually to guarantee it gets tracked.
 */
function onVideoEnded() {
	if (!videoElement.value) return;

	emits('onVideoEnded', true);

	// Replay the video
	videoElement.value.play();
}

// Set the initial muted state based on the prop
onMounted(() => {
	if (props.sraPlacement === 'BACKDROP_VIDEO') {
		isMuted.value = false;
		if (videoElement.value) {
			videoElement.value.muted = false;
		}
	}
});

return { __sfc: true,TrackingWatchedPoints, props, emits, trackSraEvent, visibility, activeRoute, sponsoredCampaign, campaignNode, disclaimerText, isVideoAsGifCampaign, shouldPositionVidAsGifToTop, videoElement, isFading, isMuted, volumeIcon, onVideoStateChanged, onMouseEnter, onMouseLeave, onVisibilityPlaybackControl, toggleMute, videoDuration, lastTrackedPoint, setVideoDuration, onTimeUpdate, onVideoEnded }
}

})